/* eslint-disable @typescript-eslint/no-explicit-any */

function eq(value: any, other: any): boolean {
  return value === other || (value !== value && other !== other); // NaN check
}

function isObjectLike(value: any): boolean {
  return value !== null && typeof value === "object";
}

function isTypedArray(value: any): boolean {
  return ArrayBuffer.isView(value) && !(value instanceof DataView);
}

function isArguments(value: any): boolean {
  return Object.prototype.toString.call(value) === "[object Arguments]";
}

function isBuffer(value: any): boolean {
  return (
    value?.constructor != null && typeof value.constructor.isBuffer === "function" && value.constructor.isBuffer(value)
  );
}

function isDate(value: any): boolean {
  return Object.prototype.toString.call(value) === "[object Date]";
}

function isRegExp(value: any): boolean {
  return Object.prototype.toString.call(value) === "[object RegExp]";
}

function equalArrays(array: any[], other: any[], customizer: any, stackA: any[], stackB: any[]): boolean {
  const length = array.length;
  if (length !== other.length) return false;

  for (let index = 0; index < length; index++) {
    if (!baseIsEqual(array[index], other[index], customizer, stackA, stackB)) {
      return false;
    }
  }
  return true;
}

function equalByTag(object: any, other: any, tag: string): boolean {
  switch (tag) {
    case "[object Boolean]":
    case "[object Date]":
      return +object === +other;

    case "[object RegExp]":
    case "[object String]":
      return object.toString() === other.toString();

    case "[object Number]":
      return (object !== +object && other !== +other) || object === +other;

    case "[object Map]": {
      if (object.size !== other.size) return false;
      for (const [key, val] of object) {
        if (!other.has(key) || !baseIsEqual(val, other.get(key))) return false;
      }
      return true;
    }

    case "[object Set]": {
      if (object.size !== other.size) return false;
      for (const val of object) {
        if (![...other].some((o) => baseIsEqual(o, val))) return false;
      }
      return true;
    }

    default:
      return false;
  }
}

function equalObjects(object: any, other: any, customizer: any, stackA: any[], stackB: any[]): boolean {
  const keysA = Object.keys(object);
  const keysB = Object.keys(other);

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(other, key)) return false;

    const valA = object[key];
    const valB = other[key];

    let compared;
    if (customizer) {
      compared = customizer(valA, valB);
    }
    if (compared === undefined && !baseIsEqual(valA, valB, customizer, stackA, stackB)) {
      return false;
    } else if (compared === false) {
      return false;
    }
  }
  return true;
}

function baseIsEqual(value: any, other: any, customizer?: any, stackA: any[] = [], stackB: any[] = []): boolean {
  if (eq(value, other)) return true;

  if (value == null || other == null) return value !== value && other !== other;

  if (!isObjectLike(value) || !isObjectLike(other)) return false;

  // Handle circular references
  const length = stackA.length;
  for (let i = 0; i < length; i++) {
    if (stackA[i] === value) return stackB[i] === other;
  }

  stackA.push(value);
  stackB.push(other);

  const tag = Object.prototype.toString.call(value);
  const otherTag = Object.prototype.toString.call(other);

  if (tag !== otherTag) return false;

  if (Array.isArray(value)) {
    return equalArrays(value, other, customizer, stackA, stackB);
  }

  if (isBuffer(value) && isBuffer(other)) {
    return equalArrays(value, other, customizer, stackA, stackB);
  }

  if (isTypedArray(value) && isTypedArray(other)) {
    return equalArrays(value, other, customizer, stackA, stackB);
  }

  if (isArguments(value) || isArguments(other)) {
    return equalObjects(value, other, customizer, stackA, stackB);
  }

  if (isDate(value) || isRegExp(value)) {
    const result = equalByTag(value, other, tag);
    stackA.pop();
    stackB.pop();
    return result;
  }

  if (tag === "[object Map]" || tag === "[object Set]" || tag === "[object ArrayBuffer]") {
    const result = equalByTag(value, other, tag);
    stackA.pop();
    stackB.pop();
    return result;
  }

  const result = equalObjects(value, other, customizer, stackA, stackB);
  stackA.pop();
  stackB.pop();
  return result;
}

export function deepEqual(a: any, b: any): boolean {
  return baseIsEqual(a, b);
}

export function deepEqualWith(a: any, b: any, customizer: (a: any, b: any) => boolean | undefined): boolean {
  return baseIsEqual(a, b, customizer);
}
